import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, inr, fmtDateTime } from "../../lib/api";
import { FullScreenLoader } from "../../context/AuthContext";

const STATUSES = ["pending", "kyc_submitted", "kyc_approved", "active", "rejected", "suspended", "blocked"];
const TABS = ["overview", "customers", "commissions", "ledger", "withdrawals"];

export default function AdminFranchiseDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/franchises/${id}`).then(({ data }) => { setData(data); setStatus(data.franchise?.status || ""); }).catch((e) => { toast.error(fmtErr(e)); setData({}); });
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (tab === "overview") return;
    setRows([]);
    api.get(`/admin/franchises/${id}/${tab}`).then(({ data }) => setRows(data)).catch((e) => toast.error(fmtErr(e)));
  }, [tab, id]);

  const applyStatus = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/franchises/${id}/status`, { status, reason });
      toast.success(`Franchise status → ${status}`);
      setReason("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const verifyBank = async (bankId, verification_status) => {
    try {
      await api.put(`/admin/franchise-bank/${bankId}/verify`, { verification_status });
      toast.success(`Bank ${verification_status}`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!data) return <FullScreenLoader label="Loading franchise" />;
  const fr = data.franchise || {};
  const w = data.wallet || {};

  return (
    <div className="space-y-5" data-testid="admin-franchise-detail">
      <Link to="/admin/franchises" data-testid="back-to-franchises" className="inline-flex items-center gap-1.5 text-sm font-semibold text-shipany-blue hover:underline"><ArrowLeft className="h-4 w-4" /> All franchises</Link>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-shipany-navy" data-testid="franchise-detail-name">{fr.name}</h1>
            <p className="text-sm text-slate-500">{fr.email} · {fr.mobile}</p>
            <p className="mt-1 text-xs text-slate-400"><span className="mono">{fr.franchise_code}</span> · {[fr.city, fr.state].filter(Boolean).join(", ") || "—"} · KYC {fr.kyc_status}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="franchise-status-controls">
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="franchise-status-select"
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-shipany-blue focus:outline-none">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="franchise-status-reason"
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-shipany-blue focus:outline-none" />
            <button onClick={applyStatus} disabled={busy || status === fr.status} data-testid="franchise-status-apply"
              className="rounded-xl bg-shipany-blue px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Apply</button>
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-4">
          <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-semibold text-slate-500">Customers</p><p className="mono text-lg font-bold">{data.counts?.customers ?? 0}</p></div>
          <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-semibold text-slate-500">Shipments</p><p className="mono text-lg font-bold">{data.counts?.shipments ?? 0}</p></div>
          <div className="rounded-xl bg-emerald-50 p-4"><p className="text-xs font-semibold text-emerald-600">Wallet Available</p><p className="mono text-lg font-bold text-emerald-700" data-testid="franchise-wallet-available">{inr(w.available ?? 0)}</p></div>
          <div className="rounded-xl bg-amber-50 p-4"><p className="text-xs font-semibold text-amber-600">Locked</p><p className="mono text-lg font-bold text-amber-700">{inr(w.locked ?? 0)}</p></div>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto" data-testid="franchise-detail-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`franchise-tab-${t}`}
            className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${tab === t ? "bg-shipany-navy text-white" : "bg-white text-slate-500 border border-slate-200"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="franchise-overview-card">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Details</h2>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-400">Contact person</dt><dd className="font-semibold">{fr.contact_person || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Address</dt><dd className="font-semibold text-right">{[fr.address, fr.city, fr.state, fr.pincode].filter(Boolean).join(", ") || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Status</dt><dd className="font-semibold capitalize">{fr.status}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Status reason</dt><dd className="font-semibold">{fr.status_reason || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Notes</dt><dd className="font-semibold">{fr.notes || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Created</dt><dd className="font-semibold">{fmtDateTime(fr.created_at)}</dd></div>
            </dl>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="franchise-bank-card">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Payout Bank Account</h2>
            {data.bank ? (
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Holder</span><span className="font-semibold">{data.bank.account_holder}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Bank</span><span className="font-semibold">{data.bank.bank_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Account</span><span className="mono font-semibold">{data.bank.account_number_masked}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">IFSC</span><span className="mono font-semibold">{data.bank.ifsc}</span></div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Verification</span>
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${data.bank.verification_status === "verified" ? "bg-emerald-100 text-emerald-700" : data.bank.verification_status === "rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`} data-testid="bank-verification-status">{data.bank.verification_status}</span>
                </div>
                {data.bank.verification_status === "pending" && (
                  <div className="flex gap-2 pt-2">
                    <button onClick={() => verifyBank(data.bank.bank_account_id, "verified")} data-testid="bank-verify-btn" className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-600 py-2 text-xs font-bold text-white"><ShieldCheck className="h-3.5 w-3.5" /> Verify</button>
                    <button onClick={() => verifyBank(data.bank.bank_account_id, "rejected")} data-testid="bank-reject-btn" className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-red-500 py-2 text-xs font-bold text-white"><ShieldAlert className="h-3.5 w-3.5" /> Reject</button>
                  </div>
                )}
              </div>
            ) : <p className="mt-3 text-xs text-slate-400" data-testid="bank-not-added">No bank account added by the franchise yet.</p>}
          </div>
        </div>
      )}

      {tab === "customers" && (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="franchise-customers-table">
          <table className="w-full min-w-[680px] text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs font-bold uppercase text-slate-400"><th className="px-5 py-3">Customer</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Phone</th><th className="px-5 py-3">KYC</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Joined</th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.user_id} className="border-b border-slate-50" data-testid={`fc-row-${c.user_id}`}>
                  <td className="px-5 py-3 font-semibold">{c.name}</td><td className="px-5 py-3 text-xs">{c.email}</td><td className="px-5 py-3 text-xs">{c.phone}</td>
                  <td className="px-5 py-3 text-xs uppercase">{c.kyc_status}</td><td className="px-5 py-3 text-xs uppercase">{c.status}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{fmtDateTime(c.created_at)}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="px-5 py-8 text-center text-xs text-slate-400">No customers</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "commissions" && (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="franchise-commissions-table">
          <table className="w-full min-w-[860px] text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs font-bold uppercase text-slate-400"><th className="px-5 py-3">Date</th><th className="px-5 py-3">Type</th><th className="px-5 py-3 text-right">Base</th><th className="px-5 py-3 text-right">Invoice</th><th className="px-5 py-3 text-right">Franchise</th><th className="px-5 py-3 text-right">Admin Share</th><th className="px-5 py-3">Status</th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.commission_id} className="border-b border-slate-50" data-testid={`fcom-row-${c.commission_id}`}>
                  <td className="px-5 py-3 text-xs text-slate-500">{fmtDateTime(c.created_at)}</td>
                  <td className="px-5 py-3 text-xs capitalize">{c.commission_type}{c.weight_slab ? ` · ${c.weight_slab}` : ""}</td>
                  <td className="mono px-5 py-3 text-right text-xs">{c.original_base_price_snapshot != null ? inr(c.original_base_price_snapshot) : "—"}</td>
                  <td className="mono px-5 py-3 text-right text-xs">{inr(c.invoice_amount_snapshot || 0)}</td>
                  <td className="mono px-5 py-3 text-right text-xs font-bold text-emerald-600">{inr(c.commission_amount)}</td>
                  <td className="mono px-5 py-3 text-right text-xs">{c.admin_additional_share != null ? inr(c.admin_additional_share) : "—"}</td>
                  <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${c.status === "credited" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>{c.status}</span></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={7} className="px-5 py-8 text-center text-xs text-slate-400">No commissions</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "ledger" && (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="franchise-ledger-table">
          <table className="w-full min-w-[760px] text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs font-bold uppercase text-slate-400"><th className="px-5 py-3">Date</th><th className="px-5 py-3">Type</th><th className="px-5 py-3 text-right">Credit</th><th className="px-5 py-3 text-right">Debit</th><th className="px-5 py-3 text-right">Balance</th><th className="px-5 py-3">By</th></tr></thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.entry_id} className="border-b border-slate-50" data-testid={`fle-row-${l.entry_id}`}>
                  <td className="px-5 py-3 text-xs text-slate-500">{fmtDateTime(l.created_at)}</td>
                  <td className="px-5 py-3 text-xs font-semibold capitalize">{String(l.transaction_type).replace(/_/g, " ")}</td>
                  <td className="mono px-5 py-3 text-right text-xs font-bold text-emerald-600">{l.credit ? inr(l.credit) : "—"}</td>
                  <td className="mono px-5 py-3 text-right text-xs font-bold text-red-500">{l.debit ? inr(l.debit) : "—"}</td>
                  <td className="mono px-5 py-3 text-right text-xs font-semibold">{inr(l.new_balance)}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{l.created_by}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="px-5 py-8 text-center text-xs text-slate-400">No ledger entries</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "withdrawals" && (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="franchise-withdrawals-table">
          <table className="w-full min-w-[680px] text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs font-bold uppercase text-slate-400"><th className="px-5 py-3">Requested</th><th className="px-5 py-3 text-right">Amount</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Reference</th><th className="px-5 py-3">Processed</th></tr></thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.withdrawal_id} className="border-b border-slate-50" data-testid={`fwd-row-${x.withdrawal_id}`}>
                  <td className="px-5 py-3 text-xs text-slate-500">{fmtDateTime(x.requested_at)}</td>
                  <td className="mono px-5 py-3 text-right text-xs font-bold">{inr(x.amount)}</td>
                  <td className="px-5 py-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold">{x.status}</span></td>
                  <td className="mono px-5 py-3 text-xs">{x.payment_reference || "—"}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{x.processed_at ? fmtDateTime(x.processed_at) : "—"}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={5} className="px-5 py-8 text-center text-xs text-slate-400">No withdrawals</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
